/**
 * WHICH surface owns the subdivision list on /region — at most one, always.
 *
 * THE DEFECT THIS EXISTS TO PREVENT. The list used to be mounted by two
 * independent booleans: the right-hand column rendered it whenever a country
 * was open, and the mobile takeover rendered it whenever a country was open AND
 * the viewport was narrow. Both are true on a phone, and the column stays
 * mounted underneath the takeover — so at 375px the page carried two complete,
 * visible, tabbable copies of all 13 provinces, neither `inert` nor
 * `aria-hidden`. A keyboard user tabbed through 26 provinces and a screen
 * reader read the list twice.
 *
 * Two independent booleans cannot express "exactly one"; a single-valued
 * function can, and that is the whole point of this module. `region.tsx` asks
 * once and renders whichever surface it is told, so the two mounts are branches
 * of one decision instead of two decisions that happen to agree most of the
 * time.
 *
 * Kept in `lib/` rather than in `region.tsx` because `region.tsx` pulls in the
 * three.js globe, and this rule has to be unit-testable without a browser.
 */

/** The view-state machine's mode, as `region.tsx` models it. */
export type RegionViewMode = 'globe' | 'flying' | 'country' | 'closing';

/**
 * `'panel'` — the desktop right-hand column, beside the 2D map.
 * `'sheet'`  — the mobile full-screen takeover, below the map.
 * `null`     — nobody renders it.
 */
export type SubdivisionListSurface = 'panel' | 'sheet' | null;

/**
 * The single owner of the subdivision list.
 *
 * Mobile skips the fly-to entirely (`openCountry` goes straight to `country`),
 * so `flying` is a desktop-only state and the takeover has nothing to show
 * during it. Desktop shows the column from the moment the camera leaves, which
 * is why `flying` counts there: the choice is on screen before the map lands.
 *
 * `closing` yields null on both, so the list is gone the instant the close
 * starts rather than lingering through the exit animation.
 */
export function subdivisionListSurface(
  mode: RegionViewMode,
  isMobile: boolean,
): SubdivisionListSurface {
  if (isMobile) return mode === 'country' ? 'sheet' : null;
  return mode === 'flying' || mode === 'country' ? 'panel' : null;
}
